import { MantineProvider } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { RouterProvider } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

import { AuthProvider } from "~/contexts/AuthProvider";
import { TeamProvider } from "~/contexts/TeamProvider";
import { FeatureFlagsProvider } from "~/featureFlags";
import { isAlliAIDomain } from "~/helpers/helpers";
import { hardLogout } from "~/utils/auth/hardLogout";
import { getWhiteLabelConfig } from "~/utils/white-label/getWhiteLabelConfig";

import App from "./App.tsx";
import { AppConfig } from "./AppConfig.ts";
import { appQueryClient } from "./configs/queryClient";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./index.css";
import router from "./router/routes.tsx";
import { WhiteLabelGuard } from "./utils/white-label";

if (AppConfig.SENTRY.DSN && AppConfig.SENTRY.ENVIRONMENT) {
  Sentry.init({
    dsn: AppConfig.SENTRY.DSN,
    environment: AppConfig.SENTRY.ENVIRONMENT,
  });
}

const init = () => {
  const element = document.getElementById("root");

  if (!element) {
    throw new Error("#root element could not be found");
  }

  const root = createRoot(element);
  const intercomAppId = AppConfig.INTERCOM_APP_ID;

  const shouldShowIntercom =
    typeof intercomAppId === "string" &&
    intercomAppId.length > 0 &&
    isAlliAIDomain();

  const featureFlagsConfig = getWhiteLabelConfig().features;

  const AppContent = (
    <AuthProvider>
      <TeamProvider>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
        <App />
      </TeamProvider>
    </AuthProvider>
  );

  root.render(
    <StrictMode>
      <FeatureFlagsProvider
        config={featureFlagsConfig}
        onConfigError={hardLogout}
      >
        <WhiteLabelGuard>
          <ThemeProvider>
            <HelmetProvider>
              <MantineProvider>
                <QueryClientProvider client={appQueryClient}>
                  {shouldShowIntercom ? (
                    <IntercomProvider appId={intercomAppId} autoBoot={false}>
                      {AppContent}
                    </IntercomProvider>
                  ) : (
                    AppContent
                  )}
                </QueryClientProvider>
              </MantineProvider>
            </HelmetProvider>
          </ThemeProvider>
        </WhiteLabelGuard>
      </FeatureFlagsProvider>
    </StrictMode>,
  );
};

init();
